/**
 * UtilityProviderCombobox - Searchable dropdown for utility providers
 *
 * Features:
 * - Search filtering (client-side, instant)
 * - Filter by country
 * - Auto-create new provider on type
 * - Shows provider location in dropdown
 */

import { useState, useMemo, useCallback } from 'react';
import { Combobox, ComboboxOption } from '@/components/ui/combobox';
import { useUtilityProviders, useFilteredProviders } from '@/hooks/useUtilityProviders';
import type { UtilityProvider, CountryCode } from '@/types/utilityProviders';
import { formatProviderLocation, COUNTRIES } from '@/types/utilityProviders';

interface UtilityProviderComboboxProps {
  /** Selected provider ID */
  value?: string;
  /** Callback when provider is selected */
  onValueChange: (providerId: string | undefined, provider?: UtilityProvider) => void;
  /** Filter providers by country */
  countryCode?: CountryCode;
  /** Filter providers by region */
  regionCode?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Whether to allow creating new providers */
  allowCreate?: boolean;
  /** Callback when creating a new provider (receives the typed name) */
  onCreateNew?: (name: string) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
}

export function UtilityProviderCombobox({
  value,
  onValueChange,
  countryCode,
  regionCode,
  placeholder = 'Select utility provider...',
  allowCreate = true,
  onCreateNew,
  disabled = false,
  className,
}: UtilityProviderComboboxProps) {
  const [searchValue] = useState('');

  // Fetch all providers (cached, ~10KB)
  const { providers, isLoading } = useUtilityProviders({ autoFetch: true });

  // Client-side filtering (instant, <1ms)
  const filteredProviders = useFilteredProviders(providers, {
    search: searchValue,
    countryCode,
    regionCode,
  });

  // Convert to combobox options format
  const options: ComboboxOption[] = useMemo(() => {
    return filteredProviders.map((provider) => ({
      value: provider.id,
      label: provider.name,
      description: formatProviderLocation(provider),
    }));
  }, [filteredProviders]);

  // Handle selection
  const handleValueChange = useCallback(
    (selectedId: string) => {
      const provider = providers.find((p) => p.id === selectedId);
      onValueChange(selectedId, provider);
    },
    [providers, onValueChange]
  );

  // Handle create new
  const handleCreateNew = useCallback(
    (name: string) => {
      if (onCreateNew) {
        onCreateNew(name);
      }
    },
    [onCreateNew]
  );

  return (
    <Combobox
      options={options}
      value={value}
      onValueChange={handleValueChange}
      placeholder={placeholder}
      searchPlaceholder="Search providers..."
      emptyMessage={
        countryCode
          ? `No providers found in ${COUNTRIES[countryCode]}`
          : 'No providers found'
      }
      disabled={disabled}
      isLoading={isLoading}
      allowCreate={allowCreate}
      onCreateNew={handleCreateNew}
      createLabel="Add provider"
      className={className}
    />
  );
}

/**
 * Simplified version that just returns the provider name (for legacy compatibility)
 */
interface UtilityProviderNameComboboxProps {
  /** Selected provider name (text) */
  value?: string;
  /** Callback when provider name changes */
  onValueChange: (name: string) => void;
  /** Filter by country */
  countryCode?: CountryCode;
  /** Placeholder */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Allow creating new */
  allowCreate?: boolean;
}

export function UtilityProviderNameCombobox({
  value,
  onValueChange,
  countryCode,
  placeholder = 'Select utility provider...',
  disabled = false,
  allowCreate = true,
}: UtilityProviderNameComboboxProps) {
  // Fetch all providers
  const { providers, isLoading, createProvider } = useUtilityProviders({ autoFetch: true });

  // Client-side filtering
  const filteredProviders = useFilteredProviders(providers, {
    countryCode,
  });

  // Convert to combobox options (use name as both value and label)
  const options: ComboboxOption[] = useMemo(() => {
    return filteredProviders.map((provider) => ({
      value: provider.name, // Use name as value for legacy compatibility
      label: provider.name,
      description: formatProviderLocation(provider),
    }));
  }, [filteredProviders]);

  // Handle create new provider
  const handleCreateNew = useCallback(
    async (name: string) => {
      // Auto-create the provider in the database
      const newProvider = await createProvider({
        name,
        countryCode: countryCode || 'US',
      });
      if (newProvider) {
        onValueChange(newProvider.name);
      }
    },
    [createProvider, countryCode, onValueChange]
  );

  return (
    <Combobox
      options={options}
      value={value}
      onValueChange={onValueChange}
      placeholder={placeholder}
      searchPlaceholder="Search providers..."
      emptyMessage="No providers found"
      disabled={disabled}
      isLoading={isLoading}
      allowCreate={allowCreate}
      onCreateNew={handleCreateNew}
      createLabel="Add provider"
    />
  );
}
